import {
  AddCircleOutline,
  AddGroupMembershipRequest,
  RemoveGroupMembershipRequest,
  ChevronLeftOutline,
  CloseCircleOutline,
  GroupMembership,
  PageEmptyState,
  SearchInput,
  UserOutline,
  EmployeeCard,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrideEmployeeInformation } from 'app/pages/RewardAndRecognition/Prides/Prides.types';

import FailedMembersSummary from 'app/components/GroupMemberships/FailedMembersSummary';

import BulkActionBar from '../../BulkActionBar/BulkActionBar';

import { PrideEmployeeListModalProps } from './PrideEmployeeListModal.types';

function ListEmployees<T>({
  fetchNextPage = undefined,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoadingMemberships = false,
  renderCard,
  items,
  onClearSearch,
  search,
}: {
  search: string;
  onClearSearch: () => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoadingMemberships?: boolean;
  renderCard: (item: T) => React.ReactNode;
  items: T[];
}) {
  const bottomRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage?.();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const el = bottomRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoadingMemberships) {
    return null;
  }

  if (items.length === 0) {
    return (
      <PageEmptyState
        {...(search
          ? {
              icon: <UserOutline boxSize="50px" color="brand.purple" />,
              title: 'No results!',
              description: 'Sorry, there are no search results.',
              ctaLabel: 'Clear search',
              onCtaClick: onClearSearch,
            }
          : {
              icon: <UserOutline boxSize="50px" color="brand.purple" />,
              title: 'No Employees Found',
              description: 'Sorry, there are no employees in this pride.',
            })}
      />
    );
  }

  return (
    <>
      {items.map((item) => renderCard(item))}
      <Box ref={bottomRef} h="1px" />
      {isFetchingNextPage && (
        <Flex justify="center" py="12px">
          <Spinner size="sm" color="brand.purple" />
        </Flex>
      )}
    </>
  );
}

function PrideEmployeeListModal({
  isOpen,
  onClose,
  selectedGroup,
  isOpenAddEmployee: isAddEmployee,
  onOpenAddEmployee,
  onCloseAddEmployee,
  onFetchNextPage,
  hasNextPage,
  isLoading,
  isLoadingNextPage,
  onAddEmployees,
  onRemoveEmployees,
  members,
  allMembers = members,
  failedMembers = [],
  isSubmitting = false,
}: PrideEmployeeListModalProps) {
  const prideName = useMemo(() => selectedGroup?.name ?? '', [selectedGroup]);

  const [selected, setSelected] = useState<PrideEmployeeInformation[]>([]);
  const [search, setSearch] = useState('');

  // Members that can be acted on: add (not in group) or remove (not primary)
  const selectableMembers = useMemo(
    () =>
      isAddEmployee
        ? allMembers.filter((m) => !m.isInSelectedGroup)
        : allMembers.filter((m) => !m.isPrimaryGroup),
    [allMembers, isAddEmployee]
  );

  const isAllSelected =
    selectableMembers.length > 0 &&
    selectableMembers.every((m) => selected.some((s) => s.individualId === m.individualId));

  const isAllSelectedIndeterminate =
    selectableMembers.length > 0 &&
    selectableMembers.some((m) => selected.some((s) => s.individualId === m.individualId)) &&
    !isAllSelected;

  const onToggleSelectAll = useCallback(() => {
    setSelected(isAllSelected ? [] : selectableMembers);
  }, [isAllSelected, selectableMembers]);

  const filteredMembers = useMemo(() => {
    const base = isAddEmployee ? allMembers.filter((m) => !m.isInSelectedGroup) : members;
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(
      (m) => m.individualName.toLowerCase().includes(q) || m.jobTitle?.toLowerCase().includes(q)
    );
  }, [members, allMembers, search, isAddEmployee]);

  const onClearSearch = useCallback(() => setSearch(''), []);

  const onRemove = useCallback(
    (member: GroupMembership) => {
      onRemoveEmployees?.([member.individualId]);
      setSelected((prevSelected) =>
        prevSelected.filter((s) => s.individualId !== member.individualId)
      );
    },
    [onRemoveEmployees]
  );

  const handleOnRemoveEmployees = useCallback(() => {
    const memberIds: RemoveGroupMembershipRequest = selected.map((s) => s.individualId);
    onRemoveEmployees?.(memberIds);
    setSelected([]);
  }, [selected, onRemoveEmployees]);

  const handleOnAddEmployees = useCallback(() => {
    // Add selected members, if the individual does not have a primary group, this will be set as their primary group
    const addMembers: AddGroupMembershipRequest = selected.map((s) => ({
      individualId: s.individualId,
      membershipRole: 'MEMBER',
      isPrimaryGroup: !s.primaryGroup,
    }));

    onAddEmployees?.(addMembers);
  }, [selected, onAddEmployees]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelected([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setSearch('');
    setSelected([]);
  }, [isAddEmployee]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent w={{ md: '600px' }} p="24px 24px 24px 24px" position="relative">
        <>
          <ModalHeader p="0">
            <Flex direction="column" gap="20px" pb="5px">
              <Flex justify="space-between" align="center">
                <Flex>
                  {isAddEmployee && (
                    <IconButton
                      aria-label="Close add employee section"
                      icon={<ChevronLeftOutline boxSize="24px" />}
                      variant="link"
                      onClick={onCloseAddEmployee}
                      color="brand.purple"
                    />
                  )}

                  <Box>
                    <Text as="h6" textStyle="h6SemiBold" color="brand.black">
                      {isAddEmployee ? `Add employees to ${prideName}` : `${prideName}: employees`}
                    </Text>
                  </Box>
                </Flex>

                <IconButton
                  aria-label="Close modal"
                  icon={<CloseCircleOutline boxSize="30px" />}
                  variant="link"
                  onClick={onClose}
                  color="brand.purple"
                />
              </Flex>

              {!isAddEmployee && (
                <Flex mt="5px" justify="space-between" align="center">
                  <Text textStyle="body" color="brand.mutedPurple">
                    {allMembers.length} employees in this pride
                  </Text>
                  <Button
                    size="sm"
                    leftIcon={<AddCircleOutline boxSize="20px" />}
                    onClick={onOpenAddEmployee}
                  >
                    Add Employees
                  </Button>
                </Flex>
              )}

              {allMembers.length > 0 && (
                <>
                  <SearchInput
                    mt="5px"
                    placeholder="Search for names or job titles"
                    searchQuery={search}
                    setSearchQuery={setSearch}
                  />

                  {!search && (
                    <Checkbox
                      ml="20px"
                      isIndeterminate={isAllSelectedIndeterminate}
                      isChecked={isAllSelected}
                      isDisabled={selectableMembers.length === 0}
                      onChange={onToggleSelectAll}
                      variant="selectAll"
                    >
                      {isAddEmployee
                        ? `Select all (${selectableMembers.length})`
                        : `Select all removable (${selectableMembers.length})`}
                    </Checkbox>
                  )}
                </>
              )}
            </Flex>
          </ModalHeader>

          <ModalBody p="0">
            <VStack pb="65px" spacing="10px" align="stretch">
              {isAddEmployee && <FailedMembersSummary failedMembers={failedMembers} />}
              <ListEmployees
                search={search}
                onClearSearch={onClearSearch}
                fetchNextPage={onFetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isLoadingNextPage}
                isLoadingMemberships={isLoading}
                items={filteredMembers}
                renderCard={(member) => (
                  <EmployeeCard
                    isAddEmployee={isAddEmployee}
                    key={member.individualId}
                    member={member}
                    onRemove={() => onRemove(member)}
                    onSelect={() => {
                      setSelected((prev) =>
                        prev.some((s) => s.individualId === member.individualId)
                          ? prev.filter((s) => s.individualId !== member.individualId)
                          : [...prev, member]
                      );
                    }}
                    currentGroup={selectedGroup?.name}
                    primaryGroup={member.primaryGroup?.name}
                    isSelected={selected.some((s) => s.individualId === member.individualId)}
                    isSelectable={
                      isAddEmployee ? !member.isInSelectedGroup : !member.isPrimaryGroup
                    }
                    isRemovable={!isAddEmployee && !member.isPrimaryGroup}
                  />
                )}
              />
            </VStack>
            <Flex
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              justify="center"
              p="24px"
              zIndex={10}
              display={selected.length > 0 ? 'flex' : 'none'}
            >
              <BulkActionBar
                onDeselectAll={() => setSelected([])}
                selectedCount={selected.length}
                cta={
                  isAddEmployee ? (
                    <Button size="md" onClick={handleOnAddEmployees} isLoading={isSubmitting}>
                      Add to Pride
                    </Button>
                  ) : (
                    <Button size="md" variant="danger" onClick={handleOnRemoveEmployees}>
                      Remove from Pride
                    </Button>
                  )
                }
              />
            </Flex>
          </ModalBody>
        </>
      </ModalContent>
    </Modal>
  );
}

export default PrideEmployeeListModal;
